import { supabase } from "../supabase";
import { getActiveOrganizationId, subscribeActiveOrg } from "../org";
import type { Category, CategoryStore } from "./types";

interface CategoryRow {
  id: string;
  code: string;
  label: string;
}

export function createSupabaseCategoryStore(): CategoryStore {
  const listeners = new Set<() => void>();

  // Cached stable snapshot — must be reference-stable between mutations.
  let snapshot: Category[] = [];

  let initialized = false;
  let loadPromise: Promise<void> | null = null;

  function emit(): void {
    for (const listener of listeners) {
      listener();
    }
  }

  function commit(next: Category[]): void {
    snapshot = next;
    emit();
  }

  async function loadFromServer(): Promise<void> {
    const { data: userData, error: userError } = await supabase.auth.getUser();
    if (userError || !userData?.user) {
      commit([]);
      return;
    }
    const orgId = getActiveOrganizationId();
    if (!orgId) {
      commit([]);
      return;
    }
    const { data, error } = await supabase
      .from("categories")
      .select("id, code, label")
      .eq("organization_id", orgId)
      .order("label", { ascending: true });
    if (error) {
      // eslint-disable-next-line no-console
      console.warn(`[categories] list failed: ${error.message}`);
      commit([]);
      return;
    }
    const next = (data as CategoryRow[] | null) ?? [];
    commit(
      next.map((r) => ({ id: r.id, code: r.code, label: r.label })),
    );
  }

  function ensureLoaded(): Promise<void> {
    if (initialized) return Promise.resolve();
    if (!loadPromise) {
      loadPromise = loadFromServer().finally(() => {
        initialized = true;
        loadPromise = null;
      });
    }
    return loadPromise;
  }

  let authSubAttached = false;
  function attachAuthListener(): void {
    if (authSubAttached) return;
    authSubAttached = true;
    supabase.auth.onAuthStateChange((event) => {
      if (event === "SIGNED_OUT") {
        initialized = false;
        commit([]);
        return;
      }
      if (event === "SIGNED_IN" || event === "TOKEN_REFRESHED") {
        initialized = false;
        void ensureLoaded();
      }
    });
    subscribeActiveOrg(() => {
      initialized = false;
      void ensureLoaded();
    });
  }

  return {
    list(): Category[] {
      if (!initialized && !loadPromise) {
        void ensureLoaded();
      }
      return snapshot;
    },
    subscribe(listener: () => void): () => void {
      attachAuthListener();
      if (!initialized && !loadPromise) {
        void ensureLoaded();
      }
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
    async refresh(): Promise<void> {
      initialized = false;
      await ensureLoaded();
    },
  };
}
